import { useState } from 'react'
import { useAuth } from '../../context/useAuth'
import { useNls } from '../../nls/nlsProvider'
import { Button } from '../Common/Button'
import { Card } from '../Common/Card'
import { confirmAccountTransfer, mapAccountTransferError } from '../../services/accountTransferService'

export interface AccountTransferConfirmPageProps {
  code: string
  confirmToken: string
}

export const AccountTransferConfirmPage: React.FC<AccountTransferConfirmPageProps> = ({
  code,
  confirmToken,
}) => {
  const { t } = useNls()
  const { currentUser, isAuthLoading } = useAuth()
  const [confirming, setConfirming] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConfirm = async () => {
    if (!code || !confirmToken) return
    setConfirming(true)
    setError(null)
    try {
      const res = await confirmAccountTransfer({ code, confirmToken })
      if (res.ok) {
        setConfirmed(true)
        if (typeof window !== 'undefined') {
          window.history.replaceState(null, '', window.location.pathname)
        }
      }
    } catch (err) {
      const mapped = mapAccountTransferError(err)
      setError(t(mapped.nlsKey))
    } finally {
      setConfirming(false)
    }
  }

  if (isAuthLoading) {
    return (
      <main className="grid min-h-dvh place-items-center bg-[var(--bg)] p-6 text-[var(--text)]">
        <Card className="max-w-md p-6 text-center">
          <p className="text-sm text-[var(--muted)]">Loading...</p>
        </Card>
      </main>
    )
  }

  return (
    <main className="grid min-h-dvh place-items-center bg-[var(--bg)] p-6 text-[var(--text)]">
      <Card className="max-w-md w-full p-6 text-center space-y-4">
        <h1 className="text-xl font-semibold">{t('NLSAccountTransferConfirmPageTitle')}</h1>

        {!currentUser ? (
          <p className="text-sm text-[var(--warning)]">
            {t('NLSAccountTransferWrongAccountWarning').replace('{email}', 'the initiating account')}
          </p>
        ) : confirmed ? (
          <div className="space-y-3">
            <p className="text-sm text-[var(--success)] font-medium">
              {t('NLSAccountTransferConfirmSuccess')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-[var(--text-secondary)]">
              {t('NLSAccountTransferExplainMoves')}
            </p>
            {error ? <p className="text-sm text-[var(--danger)] font-medium">{error}</p> : null}
            <Button
              variant="primary"
              size="md"
              fullWidth
              loading={confirming}
              disabled={confirming}
              onClick={handleConfirm}
            >
              {t('NLSAccountTransferConfirmButton')}
            </Button>
          </div>
        )}
      </Card>
    </main>
  )
}
